import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

// API Gateway URL - 使用与项目相同的API Gateway URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://4qhnaidrdj.execute-api.us-east-1.amazonaws.com/dev';

// Styled components
const FloatingButton = styled(motion.button)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #0071e3;
  color: white;
  border: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #0062c4;
  }
`;

const ChatWindow = styled(motion.div)`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 350px;
  height: 500px;
  border-radius: 10px;
  background-color: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  background-color: #0071e3;
  color: white;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
`;

const ChatBody = styled.div`
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background-color: #f5f5f7;
`;

const InputArea = styled.div`
  display: flex;
  padding: 10px;
  background-color: white;
  border-top: 1px solid #e0e0e0;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  outline: none;
  
  &:focus {
    border-color: #0071e3;
  }
`;

const SendButton = styled(IconButton)`
  margin-left: 8px;
`;

const Message = styled.div`
  margin-bottom: 10px;
  max-width: 80%;
  padding: 10px 15px;
  border-radius: 18px;
  position: relative;
  font-size: 14px;
  line-height: 1.4;
  
  ${props => props.isUser ? `
    align-self: flex-end;
    background-color: #0071e3;
    color: white;
    border-bottom-right-radius: 4px;
    margin-left: auto;
  ` : `
    align-self: flex-start;
    background-color: white;
    color: #1d1d1f;
    border-bottom-left-radius: 4px;
  `}
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LoadingIndicator = styled(CircularProgress)`
  margin: 20px auto;
  display: block;
`;

const TypingIndicator = styled.div`
  align-self: flex-start;
  background-color: #e0e0e0;
  color: #606060;
  padding: 8px 15px;
  border-radius: 18px;
  margin-bottom: 10px;
  font-size: 14px;
`;

const SystemMessage = styled.div`
  text-align: center;
  margin: 10px 0;
  font-size: 12px;
  color: #666;
  font-style: italic;
`;

const DifyChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const chatBodyRef = useRef(null);
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  
  // Dify API key - 生产环境下不需要，由Lambda代理中传递
  const DIFY_API_KEY = process.env.REACT_APP_DIFY_KEY || '';
  
  // Generate a unique and persistent user ID
  const getUserId = () => {
    let userId = localStorage.getItem('dify_user_id');
    if (!userId) {
      userId = 'user-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9);
      localStorage.setItem('dify_user_id', userId);
    }
    return userId;
  };
  
  // Function to scroll to the latest message
  const scrollToBottom = () => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  
  // Toggle chat window
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    setInput(e.target.value);
  };
  
  // Handle pressing Enter to send message
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      sendMessage();
    }
  };
  
  // Clear conversation and start a new one
  const clearConversation = () => {
    setConversationId('');
    localStorage.removeItem('dify_conversation_id');
    setMessages([]);
    setMessages([{ 
      text: 'Conversation has been reset. How can I help you?', 
      isUser: false,
      isSystem: true 
    }]);
  };
  
  // Process streaming responses
  const processStreamingResponse = async (reader) => {
    setIsTyping(true);
    let assistantResponse = '';
    
    try {
      // Buffer to accumulate incomplete JSON from the stream
      let incompleteJson = '';
      
      // Function to handle chunks of data from the stream
      const processChunk = (chunk) => {
        // Add the new chunk to our incomplete JSON buffer
        incompleteJson += chunk;
        
        // Process all complete events in the buffer
        while (incompleteJson.includes('\n\n')) {
          const splitPoint = incompleteJson.indexOf('\n\n');
          const eventData = incompleteJson.substring(0, splitPoint).trim();
          incompleteJson = incompleteJson.substring(splitPoint + 2);
          
          // Make sure we have a complete event
          if (eventData.startsWith('data: ')) {
            try {
              const jsonData = JSON.parse(eventData.substring(6));
              
              if (jsonData.event === 'message') {
                assistantResponse += jsonData.answer || '';
                
                // Update the UI with the partial response
                setMessages(prevMessages => {
                  const updatedMessages = [...prevMessages];
                  // Find the last message from the assistant and update it
                  for (let i = updatedMessages.length - 1; i >= 0; i--) {
                    if (!updatedMessages[i].isUser) {
                      updatedMessages[i].text = assistantResponse;
                      return updatedMessages;
                    }
                  }
                  // If no assistant message found, add a new one
                  return [...prevMessages, { text: assistantResponse, isUser: false }];
                });
              } else if (jsonData.event === 'message_end') {
                // Message is complete, update conversation ID for future messages
                if (jsonData.conversation_id) {
                  setConversationId(jsonData.conversation_id);
                  
                  // Save the conversation ID to localStorage
                  localStorage.setItem('dify_conversation_id', jsonData.conversation_id);
                }
              } else if (jsonData.event === 'error') {
                console.error("Error from Dify API:", jsonData.message);
                // Add error message to chat
                setMessages(prev => [...prev, { text: "Sorry, an error occurred. Please try again.", isUser: false }]);
              }
            } catch (e) {
              console.error("Error parsing JSON:", e, eventData);
            }
          }
        }
      };
      
      // Process the stream
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        // Process the chunk of data
        processChunk(decoder.decode(value, { stream: true }));
      }
      
      // Process any remaining data
      if (incompleteJson.trim() && incompleteJson.startsWith('data: ')) {
        try {
          const jsonData = JSON.parse(incompleteJson.substring(6));
          if (jsonData.event === 'message') {
            assistantResponse += jsonData.answer || '';
          }
        } catch (e) {
          console.error("Error parsing JSON:", e, incompleteJson);
        }
      }
      
    } catch (error) {
      console.error("Error processing stream:", error);
    } finally {
      setIsTyping(false);
      setLoading(false);
    }
  };
  
  // Send message to Dify API through Lambda proxy
  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { text: userMessage, isUser: true }]);
    
    // Show typing indicator
    setLoading(true);
    
    try {
      // 创建请求配置
      const requestData = {
        inputs: {}, // 必须包含inputs对象，即使为空
        query: userMessage,
        response_mode: "streaming",
        user: getUserId(), // 使用持久化的用户ID
        files: [] // 包含空文件数组
      };
      
      // 如果有会话ID，添加到请求中
      if (conversationId) {
        requestData.conversation_id = conversationId;
      } else {
        // 尝试从localStorage获取会话ID
        const savedConversationId = localStorage.getItem('dify_conversation_id');
        if (savedConversationId) {
          requestData.conversation_id = savedConversationId;
          setConversationId(savedConversationId);
        }
      }
      
      // 发送到Lambda代理
      const response = await fetch(`${API_BASE_URL}/dify/chat-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      // 处理会话不存在的情况
      if (response.status === 404) {
        const errorData = await response.json();
        
        if (errorData.code === 'not_found' && errorData.message && 
            errorData.message.includes('Conversation Not Exists')) {
          // 清除无效的会话ID
          setConversationId('');
          localStorage.removeItem('dify_conversation_id');
          
          // 添加一条提示消息
          setMessages(prev => [
            ...prev,
            { text: "Previous conversation session expired. Starting a new chat.", isUser: false, isSystem: true }
          ]);
          
          // 创建新的请求（不包含conversation_id）
          const newRequestData = {
            ...requestData,
            conversation_id: undefined
          };
          
          // 重试请求
          const retryResponse = await fetch(`${API_BASE_URL}/dify/chat-messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(newRequestData)
          });
          
          if (!retryResponse.ok) {
            const retryErrorData = await retryResponse.json();
            throw new Error(`Retry failed: ${retryResponse.status} - ${JSON.stringify(retryErrorData)}`);
          }
          
          // Add an empty message from the assistant that will be updated with the streaming response
          setMessages(prev => [...prev, { text: '', isUser: false }]);
          
          // Process the streaming response
          const reader = retryResponse.body.getReader();
          await processStreamingResponse(reader);
          return; // 重试请求后返回，不执行后续代码
        }
        
        // 其他404错误
        throw new Error(`Dify API error: ${response.status} - ${JSON.stringify(errorData)}`);
      }
      
      // 如果响应不成功（除了404以外的错误），尝试解析更详细的错误信息
      if (!response.ok) {
        try {
          const errorData = await response.json();
          console.error('Detailed API error:', errorData);
          throw new Error(`Dify API error: ${response.status} - ${JSON.stringify(errorData)}`);
        } catch (parseError) {
          throw new Error(`Dify API error: ${response.status}`);
        }
      }
      
      // Add an empty message from the assistant that will be updated with the streaming response
      setMessages(prev => [...prev, { text: '', isUser: false }]);
      
      // Process the streaming response
      const reader = response.body.getReader();
      await processStreamingResponse(reader);
      
    } catch (error) {
      console.error("Error sending message to Dify:", error);
      setMessages(prev => [
        ...prev,
        { text: "Sorry, I couldn't connect to the assistant. Please try again later.", isUser: false }
      ]);
      setLoading(false);
    }
  };
  
  return (
    <>
      <FloatingButton 
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleChat}
      >
        <ChatBubbleOutlineIcon />
      </FloatingButton>
      
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <ChatHeader>
              <h3 style={{ margin: 0 }}>{isZh ? '道择助手' : 'Dozer Assistant'}</h3>
              <HeaderActions>
                <IconButton size="small" onClick={clearConversation} style={{ color: 'white', marginRight: 5 }} title="Clear conversation">
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={toggleChat} style={{ color: 'white' }} title="Close chat">
                  <CloseIcon />
                </IconButton>
              </HeaderActions>
            </ChatHeader>
            
            <ChatBody ref={chatBodyRef}>
              <MessagesContainer>
                {/* Welcome message */}
                {messages.length === 0 && (
                  <Message isUser={false}>
                    {isZh ? '你好！有什么可以帮助您的吗？' : 'Hello! How can I assist you today?'}
                  </Message>
                )}
                
                {/* Chat messages */}
                {messages.map((message, index) => (
                  message.isSystem ? (
                    <SystemMessage key={index}>
                      {message.text}
                    </SystemMessage>
                  ) : (
                    <Message key={index} isUser={message.isUser}>
                      {message.text}
                    </Message>
                  )
                ))}
                
                {/* Typing indicator */}
                {isTyping && <TypingIndicator>{isZh ? '助手正在输入...' : 'Assistant is typing...'}</TypingIndicator>}
              </MessagesContainer>
            </ChatBody>
            
            <InputArea>
              <MessageInput
                placeholder={isZh ? '输入您的消息...' : 'Type your message...'}
                value={input}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
              <SendButton 
                color="primary" 
                onClick={sendMessage}
                disabled={!input.trim() || loading}
              >
                {loading ? <CircularProgress size={24} /> : <SendIcon />}
              </SendButton>
            </InputArea>
          </ChatWindow>
        )}
      </AnimatePresence>
    </>
  );
};

export default DifyChat; 